// src/utils/statsCalculator.js

/**
 * Calcule toutes les statistiques pour un joueur
 * Utilisé par StatsGeneral.jsx, StatsAdvanced.jsx et le générateur de PDF
 * 
 * @param {Array} allPoints - Tous les points du match (match.engineState.points)
 * @param {string} player - 'J1' ou 'J2'
 * @returns {Object} Objet contenant toutes les statistiques calculées
 */
export const calculateAllStats = (allPoints, player) => {
  console.log(`\n🎯 ========== CALCUL STATS POUR ${player} ==========`);
  console.log(`Nombre total de points: ${allPoints.length}`);

  // 🆕 Filtrer les points libres (familleCode = "?") avant tout calcul de stats
  const points = allPoints.filter(p => (p.pointData?.familleCode || '') !== '?');
  console.log(`Points après filtrage "?" : ${points.length} (${allPoints.length - points.length} points libres exclus)`);
  
  let aces = 0, dfs = 0;
  let firstServes = 0, secondServes = 0;
  let firstWon = 0, secondWon = 0;
  let breakPointsFaced = 0, breakPointsSaved = 0;
  
  // Stats retour
  let breakPointsAgainst = 0, breakPointsConverted = 0;
  let returnFirst = 0, returnFirstWon = 0;
  let returnSecond = 0, returnSecondWon = 0;
  
  // Points gagnants et fautes directes
  let winners = 0;
  let unForcedErrors = 0;
  let netErrors = 0, wideErrors = 0, longErrors = 0;
  let volleyWon = 0;
  let volleyTotal = 0;
  let gamesWon = 0;
  let gamesServing = 0;
  let gamesReturning = 0;

  // 🆕 Pressure points
  let setPointsFaced = 0, setPointsSaved = 0;
  let matchPointsFaced = 0, matchPointsSaved = 0;
  let equalityPoints = 0, equalityWon = 0;
  let advantageServing = 0, advantageServingWon = 0;
  let criticalServing = 0, criticalServingWon = 0;
  let criticalReturning = 0, criticalReturningWon = 0;
  let favorableServeGames = 0, favorableServeGamesWon = 0;
  let favorableReturnGames = 0, favorableReturnGamesWon = 0;
  let afterLossTotal = 0, afterLossWon = 0;
  let afterWinTotal = 0, afterWinWon = 0;
  let longestLoseStreak = 0;
  
  // Stats par zone
  const zoneStats = {
    first: { egalite: 0, avantage: 0, z1: 0, z2: 0, z3: 0, z4: 0, z5: 0, z6: 0 },
    firstWon: { egalite: 0, avantage: 0, z1: 0, z2: 0, z3: 0, z4: 0, z5: 0, z6: 0 },
    second: { egalite: 0, avantage: 0, z1: 0, z2: 0, z3: 0, z4: 0, z5: 0, z6: 0 },
    secondWon: { egalite: 0, avantage: 0, z1: 0, z2: 0, z3: 0, z4: 0, z5: 0, z6: 0 }
  };

  // 🆕 Stats par effet (P=Plat, S=Slicé, L=Lifté)
  const effectStats = {
    first:  { P: { total: 0, won: 0, egalite: 0, egaliteWon: 0, avantage: 0, avantageWon: 0 },
              S: { total: 0, won: 0, egalite: 0, egaliteWon: 0, avantage: 0, avantageWon: 0 },
              L: { total: 0, won: 0, egalite: 0, egaliteWon: 0, avantage: 0, avantageWon: 0 } },
    second: { P: { total: 0, won: 0, egalite: 0, egaliteWon: 0, avantage: 0, avantageWon: 0 },
              S: { total: 0, won: 0, egalite: 0, egaliteWon: 0, avantage: 0, avantageWon: 0 },
              L: { total: 0, won: 0, egalite: 0, egaliteWon: 0, avantage: 0, avantageWon: 0 } }
  };

  // 🆕 Stats par effet ET zone (P/S/L x z1..z6)
  const effectZoneStats = {
    first: {
      P: { z1: 0, z1Won: 0, z2: 0, z2Won: 0, z3: 0, z3Won: 0, z4: 0, z4Won: 0, z5: 0, z5Won: 0, z6: 0, z6Won: 0 },
      S: { z1: 0, z1Won: 0, z2: 0, z2Won: 0, z3: 0, z3Won: 0, z4: 0, z4Won: 0, z5: 0, z5Won: 0, z6: 0, z6Won: 0 },
      L: { z1: 0, z1Won: 0, z2: 0, z2Won: 0, z3: 0, z3Won: 0, z4: 0, z4Won: 0, z5: 0, z5Won: 0, z6: 0, z6Won: 0 }
    },
    second: {
      P: { z1: 0, z1Won: 0, z2: 0, z2Won: 0, z3: 0, z3Won: 0, z4: 0, z4Won: 0, z5: 0, z5Won: 0, z6: 0, z6Won: 0 },
      S: { z1: 0, z1Won: 0, z2: 0, z2Won: 0, z3: 0, z3Won: 0, z4: 0, z4Won: 0, z5: 0, z5Won: 0, z6: 0, z6Won: 0 },
      L: { z1: 0, z1Won: 0, z2: 0, z2Won: 0, z3: 0, z3Won: 0, z4: 0, z4Won: 0, z5: 0, z5Won: 0, z6: 0, z6Won: 0 }
    }
  };
  
  points.forEach((point, idx) => {
    const pointData = point.pointData || {};
    const isPlayerServing = point.server === player;
    const isPlayerWinner = point.winner === player;
    const isPlayerLoser = !isPlayerWinner;
    
    // Convertir le score en "serveur-retourneur"
    let scoreAvant = point.scoreAvantPoint || '';
    if (point.server === 'J2') {
      const parts = scoreAvant.split('-');
      if (parts.length === 2) {
        scoreAvant = `${parts[1]}-${parts[0]}`;
      }
    }
    
    const formatSet = point.formatSet || 'STD';
    const isNoAd = formatSet === 'NOAD' || formatSet === 'NOADT10';
    
    // Log pour debug
    if (idx < 5 || scoreAvant.includes('40') || scoreAvant.includes('AD')) {
      console.log(`📍 Pt${idx+1}: Server=${point.server}, Player=${player}, Serving=${isPlayerServing}, ScoreOrig=${point.scoreAvantPoint}, ScoreConv=${scoreAvant}, Winner=${point.winner}`);
    }
    
    // Aces & Services gagnants
    if (isPlayerServing && isPlayerWinner && pointData.familleCode === 'S') {
      aces++;
      console.log(`🎾 ${player} ACE/Service gagnant: familleCode=${pointData.familleCode}`);
    }
    
    // Points au filet (famille V)
    if (pointData.familleCode === 'V') {
      const isFault = pointData.resultatCode === 'F';
      const playerHitVolley = isFault ? isPlayerLoser : isPlayerWinner;
      
      if (playerHitVolley) {
        volleyTotal++;
        if (isPlayerWinner) {
          volleyWon++;
        }
      }
    }
    
    // Points gagnants
    if (isPlayerWinner && pointData.resultatCode !== 'F') {
      winners++;
    }
    
    // Fautes directes
    if (isPlayerLoser) {
      const fauteCode = pointData.fauteDirecteCode;
      if (fauteCode) {
        unForcedErrors++;
        if (fauteCode === 'F') netErrors++;
        if (fauteCode === 'C') wideErrors++;
        if (fauteCode === 'L') longErrors++;
      }
    }
    
    // STATISTIQUES SERVICE
    if (isPlayerServing) {
      // Double Fautes
      if (pointData.serviceReussite === 'SF') {
        dfs++;
      }
      
      // Balles de break sauvées
      const breakScores = ['0-40', '15-40', '30-40', '40-AD'];
      if (isNoAd) breakScores.push('40-40');
      
      if (breakScores.includes(scoreAvant)) {
        breakPointsFaced++;
        console.log(`🔴 ${player} SERVEUR BP faced: score=${scoreAvant}, winner=${point.winner}`);
        if (isPlayerWinner) {
          breakPointsSaved++;
          console.log(`✅ ${player} BP SAUVÉE!`);
        }
      }
      
      // Services par zone
      const zone = pointData.zoneCode;
      const isFirstServe = pointData.serviceReussite === '1';
      const isSecondServe = pointData.serviceReussite === '2';
      
      if (isFirstServe) {
        firstServes++;
        if (isPlayerWinner) firstWon++;
        
        if (zone) {
          const isEgalite = ['1', '2', '3'].includes(zone);
          if (isEgalite) {
            zoneStats.first.egalite++;
            if (isPlayerWinner) zoneStats.firstWon.egalite++;
          } else {
            zoneStats.first.avantage++;
            if (isPlayerWinner) zoneStats.firstWon.avantage++;
          }
          
          zoneStats.first[`z${zone}`]++;
          if (isPlayerWinner) zoneStats.firstWon[`z${zone}`]++;
        }
      }
      
      if (isSecondServe) {
        secondServes++;
        if (isPlayerWinner) secondWon++;
        
        if (zone) {
          const isEgalite = ['1', '2', '3'].includes(zone);
          if (isEgalite) {
            zoneStats.second.egalite++;
            if (isPlayerWinner) zoneStats.secondWon.egalite++;
          } else {
            zoneStats.second.avantage++;
            if (isPlayerWinner) zoneStats.secondWon.avantage++;
          }
          
          zoneStats.second[`z${zone}`]++;
          if (isPlayerWinner) zoneStats.secondWon[`z${zone}`]++;
        }
      }

      // 🆕 Stats par effet (P/S/L) sur 1er et 2nd service
      const effet = pointData.effetCode;
      if (effet && ['P', 'S', 'L'].includes(effet)) {
        const serveKey = isFirstServe ? 'first' : (isSecondServe ? 'second' : null);
        if (serveKey) {
          const isEgalite = zone && ['1', '2', '3'].includes(zone);
          const isAvantage = zone && ['4', '5', '6'].includes(zone);

          // Totaux globaux par effet
          effectStats[serveKey][effet].total++;
          if (isPlayerWinner) effectStats[serveKey][effet].won++;

          // Par côté (égalité / avantage)
          if (isEgalite) {
            effectStats[serveKey][effet].egalite++;
            if (isPlayerWinner) effectStats[serveKey][effet].egaliteWon++;
          }
          if (isAvantage) {
            effectStats[serveKey][effet].avantage++;
            if (isPlayerWinner) effectStats[serveKey][effet].avantageWon++;
          }

          // Par effet ET zone précise
          if (zone) {
            effectZoneStats[serveKey][effet][`z${zone}`]++;
            if (isPlayerWinner) effectZoneStats[serveKey][effet][`z${zone}Won`]++;
          }
        }
      }
    }
    
    // STATISTIQUES RETOUR
    if (!isPlayerServing) {
      const opponentServeType = pointData.serviceReussite;
      
      // Balles de break converties
      const breakScoresAgainst = ['0-40', '15-40', '30-40', '40-AD'];
      if (isNoAd) breakScoresAgainst.push('40-40');
      
      if (breakScoresAgainst.includes(scoreAvant)) {
        breakPointsAgainst++;
        console.log(`🟢 ${player} RETOURNEUR BP: score=${scoreAvant} (serveur-retourneur), winner=${point.winner}`);
        if (isPlayerWinner) {
          breakPointsConverted++;
          console.log(`✅ ${player} BP CONVERTIE!`);
        }
      }
      
      // Points gagnés au retour sur 1er service
      if (opponentServeType === '1') {
        returnFirst++;
        if (isPlayerWinner) returnFirstWon++;
      }
      
      // Points gagnés au retour sur 2nd service
      if (opponentServeType === '2') {
        returnSecond++;
        if (isPlayerWinner) returnSecondWon++;
      }
    }

  });

  // 🆕 PRESSURE POINTS — boucle sur allPoints (inclut les points libres "?")
  const criticalScoresServing   = ['0-30', '0-40', '15-40', '30-40', '40-AD'];
  const criticalScoresReturning = ['30-0', '40-0', '40-15', '40-30', '40-AD'];

  allPoints.forEach((point, idx) => {
    const isPlayerServing = point.server === player;
    const isPlayerWinner  = point.winner === player;
    // Convertir score en serveur-retourneur
    let scoreAvant = point.scoreAvantPoint || '';
    if (point.server === 'J2') {
      const parts = scoreAvant.split('-');
      if (parts.length === 2) scoreAvant = `${parts[1]}-${parts[0]}`;
    }

    // 4 - Égalité (40-40)
    if (scoreAvant === '40-40') {
      equalityPoints++;
      if (isPlayerWinner) equalityWon++;
    }

    // 5 - Avantage au service (serveur mène AD-40)
    if (isPlayerServing && scoreAvant === 'AD-40') {
      advantageServing++;
      if (isPlayerWinner) advantageServingWon++;
    }

    // 6 - Situation critique pour le serveur
    if (isPlayerServing && criticalScoresServing.includes(scoreAvant)) {
      criticalServing++;
      if (isPlayerWinner) criticalServingWon++;
    }

    // 7 - Situation critique pour le retourneur
    if (!isPlayerServing && criticalScoresReturning.includes(scoreAvant)) {
      criticalReturning++;
      if (isPlayerWinner) criticalReturningWon++;
    }

    // 10 - Réaction / 11 - Confirmation (sur 2 points consécutifs)
    if (idx >= 2) {
      const prev1 = allPoints[idx - 1];
      const prev2 = allPoints[idx - 2];
      if (prev1.winner !== player && prev2.winner !== player) {
        afterLossTotal++;
        if (isPlayerWinner) afterLossWon++;
      } else if (prev1.winner === player && prev2.winner === player) {
        afterWinTotal++;
        if (isPlayerWinner) afterWinWon++;
      }
    }
  });

  // CALCUL DES JEUX GAGNÉS
  // 🆕 Utiliser allPoints (pas points) pour le calcul des jeux car les points libres
  // font bien avancer le score et doivent être pris en compte pour les jeux gagnés
  for (let i = 0; i < allPoints.length; i++) {
    const point = allPoints[i];
    const nextPoint = allPoints[i + 1];
    
    const isGameEnd = nextPoint && (
      nextPoint.scoreAvantPoint === '0-0' || 
      (nextPoint.setNumero !== point.setNumero)
    );
    
    if (isGameEnd && point.winner === player) {
      gamesWon++;
      if (point.server === player) {
        gamesServing++;
      } else {
        gamesReturning++;
      }
    }
  }
  
  // 🆕 BALLES DE SET / MATCH — logique basée sur analyzeSetPoint de pointContextAnalyzer.js
  // Fonctions utilitaires (même logique que pointContextAnalyzer)
  const _countSetOrMatchBalls = (scoreConv, fmtSet) => {
    const isNoAd = fmtSet === 'NOAD' || fmtSet === 'NOADT10';
    const [serverScore] = scoreConv.split('-');
    const hasAD = scoreConv.includes('AD');
    if (hasAD) {
      if (serverScore === 'AD') return 1;
      if (scoreConv === '40-AD') return 0;
    }
    if (isNoAd && scoreConv === '40-40') return 1;
    const noadBonus = isNoAd ? 1 : 0;
    if (scoreConv === '40-0') return 3 + noadBonus;
    if (scoreConv === '40-15') return 2 + noadBonus;
    if (scoreConv === '40-30') return 1 + noadBonus;
    return 0;
  };

  const _countBreakBalls = (scoreConv, isNoAd) => {
    const [, receiverScore] = scoreConv.split('-');
    const hasAD = scoreConv.includes('AD');
    if (hasAD) {
      if (receiverScore === 'AD') return 1;
      if (scoreConv === '40-AD') return 1;
      if (scoreConv === 'AD-40') return 0;
    }
    if (isNoAd && scoreConv === '40-40') return 1;
    if (scoreConv === '0-40') return 3;
    if (scoreConv === '15-40') return 2;
    if (scoreConv === '30-40') return 1;
    return 0;
  };

  // Reconstruire les scores de set point par point pour avoir j1Games/j2Games à chaque instant
  {
    const runningSetScores = {}; // { setNum: [j1, j2] }
    
    for (let i = 0; i < allPoints.length; i++) {
      const pt = allPoints[i];
      const prevPt = allPoints[i - 1];
      const setNum = pt.setNumero;
      const fmtSet = pt.formatSet || 'STD';
      const isNoAd = fmtSet === 'NOAD' || fmtSet === 'NOADT10';
      const isPlayerServing = pt.server === player;
      const isPlayerWinner = pt.winner === player;

      // Initialiser le score du set
      if (!runningSetScores[setNum]) runningSetScores[setNum] = [0, 0];

      // Mettre à jour après chaque fin de jeu dans ce set
      if (prevPt && prevPt.setNumero === setNum && prevPt.scoreApresPoint === '0-0') {
        if (prevPt.winner === 'J1') runningSetScores[setNum][0]++;
        else runningSetScores[setNum][1]++;
      }

      const [j1G, j2G] = runningSetScores[setNum];

      // Score du jeu converti en serveur-retourneur
      let scoreConv = pt.scoreAvantPoint || '';
      if (pt.server === 'J2') {
        const parts = scoreConv.split('-');
        if (parts.length === 2) scoreConv = `${parts[1]}-${parts[0]}`;
      }

      // Déterminer le leader du set
      const leader = j1G > j2G ? 'J1' : (j2G > j1G ? 'J2' : null);
      const leaderG = leader === 'J1' ? j1G : j2G;
      const trailerG = leader === 'J1' ? j2G : j1G;

      // Balle de set CONTRE notre joueur = l'adversaire peut gagner le set sur ce point
      // Notre joueur doit la sauver
      const opponent = player === 'J1' ? 'J2' : 'J1';
      const opponentLeads = leader === opponent && leaderG >= 5 && !(leaderG === 6 && trailerG === 6);

      if (opponentLeads) {
        // Cas 1 : adversaire sert ET mène → balle de set sur score de jeu favorable pour l'adversaire
        if (!isPlayerServing) {
          // Notre joueur retourne, adversaire sert → score converti du point de vue adversaire
          const balls = _countSetOrMatchBalls(scoreConv, fmtSet);
          if (balls > 0) {
            setPointsFaced++;
            if (isPlayerWinner) setPointsSaved++;
          }
        }
        // Cas 2 : adversaire retourne ET mène → balle de set sur score de balle de break pour l'adversaire
        else if (isPlayerServing) {
          // Notre joueur sert, adversaire retourne → balle de break pour l'adversaire = balle de set
          const balls = _countBreakBalls(scoreConv, isNoAd);
          if (balls > 0) {
            setPointsFaced++;
            if (isPlayerWinner) setPointsSaved++;
          }
        }
      }

      // Balle de match : même logique mais en tenant compte du score de sets
      // Calculer les sets gagnés actuels
      let j1Sets = 0, j2Sets = 0;
      for (const [sn, [s1, s2]] of Object.entries(runningSetScores)) {
        if (parseInt(sn) < setNum) { // sets déjà terminés
          if (s1 > s2) j1Sets++; else if (s2 > s1) j2Sets++;
        }
      }
      const setsToWin = (pt.formatMatch === 'BEST_OF_5') ? 3 : 2;
      const playerSets = player === 'J1' ? j1Sets : j2Sets;
      const opponentSets = player === 'J1' ? j2Sets : j1Sets;
      const isMatchDecisiveSet = (playerSets === setsToWin - 1) || (opponentSets === setsToWin - 1);

      if (isMatchDecisiveSet) {
        // Balle de match CONTRE notre joueur = l'adversaire peut gagner le match
        const opponentLeadsMatch = leader === opponent && leaderG >= 5 && !(leaderG === 6 && trailerG === 6);
        if (opponentLeadsMatch) {
          if (!isPlayerServing) {
            const balls = _countSetOrMatchBalls(scoreConv, fmtSet);
            if (balls > 0) {
              matchPointsFaced++;
              if (isPlayerWinner) matchPointsSaved++;
            }
          } else if (isPlayerServing) {
            const balls = _countBreakBalls(scoreConv, isNoAd);
            if (balls > 0) {
              matchPointsFaced++;
              if (isPlayerWinner) matchPointsSaved++;
            }
          }
        }
      }
    }
  }


  // 🆕 CALCUL PRESSURE POINTS sur les jeux (balles de set/match + jeux favorables)
  // On parcourt allPoints pour analyser les fins de jeux et détecter les situations
  for (let i = 0; i < allPoints.length; i++) {
    const pt = allPoints[i];
    const nextPt = allPoints[i + 1];
    const isServing = pt.server === player;
    const isWinner = pt.winner === player;

    // Convertir score (serveur-retourneur)
    let score = pt.scoreAvantPoint || '';
    if (pt.server === 'J2') {
      const parts = score.split('-');
      if (parts.length === 2) score = `${parts[1]}-${parts[0]}`;
    }

    // Détecter fin de jeu : le score suivant repasse à 0-0
    // OU c'est le dernier point ET le score après repasse à 0-0
    const isGameEnd = pt.scoreApresPoint === '0-0' || 
      (nextPt && nextPt.scoreAvantPoint === '0-0') ||
      (nextPt && nextPt.setNumero !== pt.setNumero) ||
      (!nextPt && pt.scoreApresPoint === '0-0');

    // 2/3 - Balles de set/match : gérées en pré-passe ci-dessous (hors boucle)

    // 8 - Jeux de service gagnés en situation très favorable
    // Situations favorables serveur : 30-0, 40-0, 40-15 (du point de vue serveur)
    if (isServing && isGameEnd && isWinner) {
      // Vérifier si à un moment dans ce jeu le serveur a mené 30-0, 40-0 ou 40-15
      // On cherche dans les points précédents du même jeu
      let hadFavorable = false;
      // Remonter dans les points du même jeu (même setNumero, score pas encore remis à 0-0)
      for (let j = i; j >= 0; j--) {
        if (allPoints[j].setNumero !== pt.setNumero) break;
        // Si on trouve un point dont le scoreApresPoint est "0-0" avant j=i, c'est un jeu précédent
        if (j < i && allPoints[j].scoreApresPoint === '0-0') break;
        let s = allPoints[j].scoreAvantPoint || '';
        if (allPoints[j].server === 'J2') {
          const parts = s.split('-');
          if (parts.length === 2) s = `${parts[1]}-${parts[0]}`;
        }
        if (['30-0', '40-0', '40-15'].includes(s)) { hadFavorable = true; break; }
      }
      if (hadFavorable) {
        favorableServeGames++;
        favorableServeGamesWon++;
      }
    } else if (isServing && isGameEnd && !isWinner) {
      let hadFavorable = false;
      for (let j = i; j >= 0; j--) {
        if (allPoints[j].setNumero !== pt.setNumero) break;
        if (j < i && allPoints[j].scoreApresPoint === '0-0') break;
        let s = allPoints[j].scoreAvantPoint || '';
        if (allPoints[j].server === 'J2') {
          const parts = s.split('-');
          if (parts.length === 2) s = `${parts[1]}-${parts[0]}`;
        }
        if (['30-0', '40-0', '40-15'].includes(s)) { hadFavorable = true; break; }
      }
      if (hadFavorable) favorableServeGames++;
    }

    // 9 - Jeux de retour gagnés en situation très favorable
    // Situations favorables retourneur : 0-30, 0-40, 15-40 (du point de vue serveur = retourneur mène)
    if (!isServing && isGameEnd && isWinner) {
      let hadFavorable = false;
      for (let j = i; j >= 0; j--) {
        if (allPoints[j].setNumero !== pt.setNumero) break;
        if (j < i && allPoints[j].scoreApresPoint === '0-0') break;
        let s = allPoints[j].scoreAvantPoint || '';
        if (allPoints[j].server === 'J2') {
          const parts = s.split('-');
          if (parts.length === 2) s = `${parts[1]}-${parts[0]}`;
        }
        if (['0-30', '0-40', '15-40'].includes(s)) { hadFavorable = true; break; }
      }
      if (hadFavorable) {
        favorableReturnGames++;
        favorableReturnGamesWon++;
      }
    } else if (!isServing && isGameEnd && !isWinner) {
      let hadFavorable = false;
      for (let j = i; j >= 0; j--) {
        if (allPoints[j].setNumero !== pt.setNumero) break;
        if (j < i && allPoints[j].scoreApresPoint === '0-0') break;
        let s = allPoints[j].scoreAvantPoint || '';
        if (allPoints[j].server === 'J2') {
          const parts = s.split('-');
          if (parts.length === 2) s = `${parts[1]}-${parts[0]}`;
        }
        if (['0-30', '0-40', '15-40'].includes(s)) { hadFavorable = true; break; }
      }
      if (hadFavorable) favorableReturnGames++;
    }
  }

  // 12 - Plus longue série de points consécutifs perdus
  let currentLoseStreak = 0;
  for (const pt of allPoints) {
    if (pt.winner !== player) {
      currentLoseStreak++;
      if (currentLoseStreak > longestLoseStreak) longestLoseStreak = currentLoseStreak;
    } else {
      currentLoseStreak = 0;
    }
  }

  // LOGS DEBUG
  console.log(`\n📊 === STATS ${player} ===`);
  console.log(`Aces (familleCode=S): ${aces}`);
  console.log(`1er services: ${firstServes}, Points gagnés 1er serv: ${firstWon}`);
  console.log(`2nd services: ${secondServes}, Points gagnés 2nd serv: ${secondWon}`);
  console.log(`Doubles fautes: ${dfs}`);
  console.log(`Break faced: ${breakPointsFaced}, saved: ${breakPointsSaved}`);
  console.log(`Break against: ${breakPointsAgainst}, converted: ${breakPointsConverted}`);
  console.log(`Volley gagnés: ${volleyWon}/${volleyTotal}`);
  console.log(`Jeux gagnés: ${gamesWon} (service: ${gamesServing}, retour: ${gamesReturning})`);
  
  // Calculs de pourcentages
  const totalServeAttempts = firstServes + secondServes + dfs;
  const firstPct = totalServeAttempts > 0 ? Math.round((firstServes / totalServeAttempts) * 100) : 0;
  console.log(`% 1er service: ${firstServes}/${totalServeAttempts} = ${firstPct}%`);
  
  const firstWonPct = firstServes > 0 ? Math.round((firstWon / firstServes) * 100) : 0;
  const secondWonPct = secondServes > 0 ? Math.round((secondWon / secondServes) * 100) : 0;
  const breakSavedPct = breakPointsFaced > 0 ? Math.round((breakPointsSaved / breakPointsFaced) * 100) : 0;
  const breakConvertedPct = breakPointsAgainst > 0 ? Math.round((breakPointsConverted / breakPointsAgainst) * 100) : 0;
  const returnFirstPct = returnFirst > 0 ? Math.round((returnFirstWon / returnFirst) * 100) : 0;
  const returnSecondPct = returnSecond > 0 ? Math.round((returnSecondWon / returnSecond) * 100) : 0;
  
  const totalPointsWon = allPoints.filter(p => p.winner === player).length;
  const totalServePoints = firstServes + secondServes;
  const totalServeWon = firstWon + secondWon;
  const serviceWonPct = totalServePoints > 0 ? Math.round((totalServeWon / totalServePoints) * 100) : 0;
  const totalReturnPoints = returnFirst + returnSecond;
  const totalReturnWon = returnFirstWon + returnSecondWon;
  const returnWonPct = totalReturnPoints > 0 ? Math.round((totalReturnWon / totalReturnPoints) * 100) : 0;
  const volleyWonPct = volleyTotal > 0 ? Math.round((volleyWon / volleyTotal) * 100) : 0;
  
  console.log(`% serv: ${totalServeWon}/${totalServePoints} = ${serviceWonPct}%`);
  console.log(`% retour: ${totalReturnWon}/${totalReturnPoints} = ${returnWonPct}%`);
  console.log(`% filet: ${volleyWon}/${volleyTotal} = ${volleyWonPct}%`);
  
  return {
    aces,
    dfs,
    breakPointsFaced,
    breakPointsSaved,
    breakSavedPct,
    firstPct,
    firstWon,
    firstWonPct,
    firstServes,
    secondWon,
    secondWonPct,
    secondServes,
    totalServeAttempts,
    breakPointsAgainst,
    breakPointsConverted,
    breakConvertedPct,
    returnFirst,
    returnFirstWon,
    returnFirstPct,
    returnSecond,
    returnSecondWon,
    returnSecondPct,
    winners,
    unForcedErrors,
    netErrors,
    wideErrors,
    longErrors,
    volleyWon,
    volleyTotal,
    serviceWonPct,
    totalServeWon,
    totalServePoints,
    returnWonPct,
    totalReturnWon,
    totalReturnPoints,
    volleyWonPct,
    gamesWon,
    gamesServing,
    gamesReturning,
    totalPoints: totalPointsWon,
    zoneStats,
    effectStats,
    effectZoneStats,
    // 🆕 Pressure points
    setPointsFaced,
    setPointsSaved,
    setPointsSavedPct: setPointsFaced > 0 ? Math.round((setPointsSaved / setPointsFaced) * 100) : 0,
    matchPointsFaced,
    matchPointsSaved,
    matchPointsSavedPct: matchPointsFaced > 0 ? Math.round((matchPointsSaved / matchPointsFaced) * 100) : 0,
    equalityPoints,
    equalityWon,
    equalityWonPct: equalityPoints > 0 ? Math.round((equalityWon / equalityPoints) * 100) : 0,
    advantageServing,
    advantageServingWon,
    advantageServingWonPct: advantageServing > 0 ? Math.round((advantageServingWon / advantageServing) * 100) : 0,
    criticalServing,
    criticalServingWon,
    criticalServingWonPct: criticalServing > 0 ? Math.round((criticalServingWon / criticalServing) * 100) : 0,
    criticalReturning,
    criticalReturningWon,
    criticalReturningWonPct: criticalReturning > 0 ? Math.round((criticalReturningWon / criticalReturning) * 100) : 0,
    favorableServeGames,
    favorableServeGamesWon,
    favorableServeGamesWonPct: favorableServeGames > 0 ? Math.round((favorableServeGamesWon / favorableServeGames) * 100) : 0,
    favorableReturnGames,
    favorableReturnGamesWon,
    favorableReturnGamesWonPct: favorableReturnGames > 0 ? Math.round((favorableReturnGamesWon / favorableReturnGames) * 100) : 0,
    afterLossTotal,
    afterLossWon,
    afterLossWonPct: afterLossTotal > 0 ? Math.round((afterLossWon / afterLossTotal) * 100) : 0,
    afterWinTotal,
    afterWinWon,
    afterWinWonPct: afterWinTotal > 0 ? Math.round((afterWinWon / afterWinTotal) * 100) : 0,
    longestLoseStreak
  };
};